import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'oidc-react';
import SessionTimeoutModal from '../../components/Modal/SessionTimeoutModal';
import { IDLE_TIMEOUT_MS, IDLE_WARNING_MS } from '../../common/config/session';
import { logoutUser } from '../../common/Utils/session';

// Shared across tabs so activity in one tab keeps the others signed in too.
export const LAST_ACTIVITY_KEY = 'lastActivityAt';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'scroll', 'touchstart'];
const ACTIVITY_WRITE_THROTTLE_MS = 5000;
const CHECK_INTERVAL_MS = 1000;

const readStoredActivity = (): number => {
  try {
    return Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeStoredActivity = (timestamp: number) => {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(timestamp));
  } catch {
    // storage unavailable — this tab's in-memory timestamp still applies
  }
};

/**
 * Signs the user out after IDLE_TIMEOUT_MS of inactivity, with a countdown warning for the
 * last IDLE_WARNING_MS. Also owns access-token renewal (automaticSilentRenew is off in App.tsx):
 * the refresh token is only used when the access token is expiring AND the user is still active.
 */
const IdleSessionManager = () => {
  const { userManager } = useAuth();
  const lastActivityRef = useRef(Date.now());
  const warningVisibleRef = useRef(false);
  const loggingOutRef = useRef(false);
  const renewingRef = useRef(false);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  const getLastActivity = () => Math.max(lastActivityRef.current, readStoredActivity());
  const isIdle = () => Date.now() - getLastActivity() >= IDLE_TIMEOUT_MS;

  const markActive = useCallback((force = false) => {
    const now = Date.now();
    if (!force && now - lastActivityRef.current < ACTIVITY_WRITE_THROTTLE_MS) return;
    lastActivityRef.current = now;
    writeStoredActivity(now);
  }, []);

  const logout = useCallback(() => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    logoutUser(userManager);
  }, [userManager]);

  // Track activity. While the warning is up, passive activity (e.g. an accidental mouse move)
  // doesn't dismiss it — the user has to click "Stay signed in" (or be active in another tab).
  useEffect(() => {
    markActive(true);
    const onActivity = () => {
      if (!warningVisibleRef.current) markActive();
    };
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, onActivity, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, onActivity));
  }, [markActive]);

  // Idle countdown: show the warning, then sign out.
  useEffect(() => {
    const check = () => {
      const remainingMs = IDLE_TIMEOUT_MS - (Date.now() - getLastActivity());
      if (remainingMs <= 0) {
        logout();
        return;
      }
      const showWarning = remainingMs <= IDLE_WARNING_MS;
      warningVisibleRef.current = showWarning;
      setSecondsLeft(showWarning ? Math.ceil(remainingMs / 1000) : null);
    };
    const interval = window.setInterval(check, CHECK_INTERVAL_MS);
    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logout]);

  // Activity-gated token renewal.
  useEffect(() => {
    if (!userManager) return;

    const renewIfActive = async () => {
      if (renewingRef.current || loggingOutRef.current) return;
      if (isIdle()) {
        logout();
        return;
      }
      renewingRef.current = true;
      try {
        await userManager.signinSilent();
      } catch (error) {
        console.error('[IdleSessionManager] Token renewal failed, signing out.', error);
        logout();
      } finally {
        renewingRef.current = false;
      }
    };

    userManager.events.addAccessTokenExpiring(renewIfActive);
    // Covers a tab that was asleep/suspended through the "expiring" window.
    userManager.events.addAccessTokenExpired(renewIfActive);
    return () => {
      userManager.events.removeAccessTokenExpiring(renewIfActive);
      userManager.events.removeAccessTokenExpired(renewIfActive);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userManager, logout]);

  const handleStaySignedIn = () => {
    warningVisibleRef.current = false;
    markActive(true);
    setSecondsLeft(null);
  };

  return (
    <SessionTimeoutModal show={secondsLeft !== null} secondsLeft={secondsLeft ?? 0} onStaySignedIn={handleStaySignedIn} onLogout={logout} />
  );
};

export default IdleSessionManager;
